/**
 * Routes for the account-level screens (Clients, Invoices, Client Quotes,
 * Sales Pipeline), which sit above any single project and render inside
 * AccountShell instead of the per-project Sidebar + Header.
 *
 * Creating or editing a client, invoice, quote, payment or lead used to open
 * a popup, so there was nothing to bookmark and a reload lost the form. Each
 * of those is now its own page with its own URL:
 *
 *   /clients/new                /clients/CL-<id>
 *   /invoices/new               /invoices/IV-<id>        /invoices/IV-<id>/payment
 *   /client-quotes/new          /client-quotes/CQ-<id>
 *   /pipeline/new-lead
 *   /workspace/projects/PJ-<id>/edit       (Details)
 *   /workspace/projects/PJ-<id>/billing    (Billing)
 *   /workspace/users/PE-<id>/edit          (Edit user: a person, their login and their roles per project)
 *
 * Also account-level, with no form pages: /vendor-directory, /workspace/users
 * (alias /users) and /workspace/settings.
 *
 * The id behind the prefix is opaque (a uuid), so it goes through parseId,
 * never Number().
 */
import { fmtId, parseId } from './jiraId';
import { entityPath } from './entityRoutes';

/** Every account screen. The list is the source of the type, so a test can walk all of them. */
export const ACCOUNT_VIEWS = [
  'overview',
  'projects',
  'account-calendar',
  'pipeline',
  'clients',
  'invoices',
  'client-quotes',
  'vendor-directory',
  'workspace-team',
  'workspace-settings',
  'client-form',
  'invoice-form',
  'invoice-payment-form',
  'client-quote-form',
  'lead-form',
  'project-edit',
  'user-edit',
] as const;

export type AccountView = typeof ACCOUNT_VIEWS[number];

export interface AccountRoute {
  view: AccountView;
  /** The record being edited; null on a list page or a "new" form page. */
  id: string | null;
  /** Project edit page only: which tab the URL opens. */
  tab?: ProjectEditTab;
}

export type ProjectEditTab = 'details' | 'billing';

const LIST_VIEWS: Record<string, AccountView> = {
  '/overview': 'overview',
  '/projects': 'projects',
  '/account-calendar': 'account-calendar',
  '/pipeline': 'pipeline',
  '/clients': 'clients',
  '/invoices': 'invoices',
  '/client-quotes': 'client-quotes',
  '/vendor-directory': 'vendor-directory',
  // Workspace admin. /settings is already the per-project Settings screen, so
  // the workspace's own live under /workspace; /users is kept as a short link.
  '/workspace/users': 'workspace-team',
  '/users': 'workspace-team',
  '/workspace/settings': 'workspace-settings',
};

/** Detail/new form routes: base path, the view they open, and the id prefix. */
const FORM_VIEWS: { base: string; view: AccountView; prefix: string }[] = [
  { base: '/clients/', view: 'client-form', prefix: 'CL' },
  { base: '/invoices/', view: 'invoice-form', prefix: 'IV' },
  { base: '/client-quotes/', view: 'client-quote-form', prefix: 'CQ' },
];

const PROJECT_BASE = '/workspace/projects/';
const PERSON_BASE = '/workspace/users/';

/** Which account screen a pathname belongs to, or null if it is not one. */
export function matchAccountRoute(pathname: string): AccountRoute | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (LIST_VIEWS[path]) return { view: LIST_VIEWS[path], id: null };
  if (path === '/pipeline/new-lead') return { view: 'lead-form', id: null };

  if (path.startsWith(PROJECT_BASE)) {
    const [segment, sub, ...extra] = path.slice(PROJECT_BASE.length).split('/');
    const tab = sub === 'edit' ? 'details' : sub === 'billing' ? 'billing' : null;
    if (!segment || !tab || extra.length > 0) return null;
    // A malformed id still lands on the page, which says "not found".
    return { view: 'project-edit', id: parseId('PJ', segment) ?? null, tab };
  }

  if (path.startsWith(PERSON_BASE)) {
    const [segment, sub, ...extra] = path.slice(PERSON_BASE.length).split('/');
    if (!segment || sub !== 'edit' || extra.length > 0) return null;
    // A malformed id still lands on the page, which says "not found".
    return { view: 'user-edit', id: parseId('PE', segment) ?? null };
  }

  for (const { base, view, prefix } of FORM_VIEWS) {
    if (!path.startsWith(base)) continue;
    const [segment, sub, ...extra] = path.slice(base.length).split('/');
    if (!segment || extra.length > 0) return null;
    if (segment === 'new') return sub ? null : { view, id: null };
    const id = parseId(prefix, segment) ?? null;
    if (!id) return null;
    if (!sub) return { view, id };
    if (base === '/invoices/' && sub === 'payment') return { view: 'invoice-payment-form', id };
    return null;
  }
  return null;
}

export const NEW_CLIENT_PATH = '/clients/new';
export const NEW_INVOICE_PATH = '/invoices/new';
export const NEW_CLIENT_QUOTE_PATH = '/client-quotes/new';
export const NEW_LEAD_PATH = '/pipeline/new-lead';

export const clientPath = (id: string) => entityPath('client', id) ?? '/clients';
export const clientInvoicePath = (id: string) => entityPath('clientInvoice', id) ?? '/invoices';
export const clientQuotePath = (id: string) => entityPath('clientQuote', id) ?? '/client-quotes';
export const editProjectPath = (id: string) => `/workspace/projects/${fmtId('PJ', id)}/edit`;
export const editPersonPath = (id: string) => `${PERSON_BASE}${fmtId('PE', id)}/edit`;
export const projectBillingPath = (id: string) => `/workspace/projects/${fmtId('PJ', id)}/billing`;
export const invoicePaymentPath = (id: string) => `${clientInvoicePath(id)}/payment`;

const NAV_ITEM: Partial<Record<AccountView, AccountView>> = {
  'client-form': 'clients',
  'invoice-form': 'invoices',
  'invoice-payment-form': 'invoices',
  'client-quote-form': 'client-quotes',
  'lead-form': 'pipeline',
  'project-edit': 'projects',
  'user-edit': 'workspace-team',
};

/** The sidebar item to highlight: a form page lights up the list it belongs to. */
export const accountNavItem = (view: AccountView): AccountView => NAV_ITEM[view] ?? view;
